// Author: Ahsan Saleem
/**
 * BloomOS V2 status registry — ONE status vocabulary for the whole platform.
 *
 * The audit found 23 private `*Tone()` functions, each re-deriving status→colour
 * for a single object type, with adjacent screens disagreeing on meaning and no
 * legend anywhere. This is the single catalogue that replaces them: given an
 * object type and a raw status string it returns a semantic tone, a human label,
 * and whether the state is terminal. Every status renders through a StatusTag
 * (icon + label), so meaning never depends on colour alone.
 */
import type { Tone } from '../ui/primitives.js';

export interface StatusMeta { tone: Tone; label: string; terminal: boolean }

/** Keyword → tone fallback, so an unmapped status still reads sensibly. Ordered:
 *  first match wins, so put the strongest signals first. */
const KEYWORD_TONE: Array<[RegExp, Tone]> = [
  [/reject|fail|cancel|void|error|overdue|breach|shortfall|blocked|critical|expired/i, 'critical'],
  [/simulat|virtual/i, 'sim'],
  [/offline|disconnect|unreachable/i, 'offline'],
  [/hold|quarantine|exception|variance|pending|await|review|partial|draft|open|unmatched|stale|caution|due/i, 'caution'],
  [/approv|post|issued?|match|reconcil|deliver|complet|verif|releas|paid|filed|active|confirm|receiv|done|closed|pass|healthy|ready|ok/i, 'success'],
  [/sourc|allocat|dispatch|progress|running|sent|submitted|new|picking|packing/i, 'info'],
];

const titleCase = (raw: string): string => raw.replace(/[_.]+/g, ' ').trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const fallbackTone = (raw: string): Tone => {
  for (const [re, tone] of KEYWORD_TONE) if (re.test(raw)) return tone;
  return 'neutral';
};

const TERMINAL = /reject|cancel|void|closed|locked|filed|delivered|reconciled|acknowledged|verified|recovered|released|matched|superseded|reversed|paid/i;

/** Explicit overrides where the keyword heuristic is wrong, ambiguous, or the label
 *  should read domain-specific. Keyed `${objectType}:${STATUS_UPPER}`. */
const REGISTRY: Record<string, StatusMeta> = {
  // farm recovery loop
  'alert:OPEN': { tone: 'critical', label: 'Open', terminal: false },
  'alert:ACKNOWLEDGED': { tone: 'caution', label: 'Acknowledged', terminal: false },
  'alert:RECOVERED': { tone: 'success', label: 'Recovered', terminal: true },
  'task:PENDING': { tone: 'caution', label: 'Pending', terminal: false },
  'task:IN_PROGRESS': { tone: 'info', label: 'In progress', terminal: false },
  'task:COMPLETED': { tone: 'info', label: 'Completed', terminal: false },
  'task:VERIFIED': { tone: 'success', label: 'Verified', terminal: true },
  'task:REJECTED': { tone: 'critical', label: 'Rejected', terminal: false },
  'batch:GROWING': { tone: 'success', label: 'Growing', terminal: false },
  'batch:HARVESTING': { tone: 'info', label: 'Harvesting', terminal: false },
  // procure to stock
  'requisition:DRAFT': { tone: 'neutral', label: 'Draft', terminal: false },
  'requisition:SUBMITTED': { tone: 'info', label: 'Submitted', terminal: false },
  'requisition:SOURCING': { tone: 'info', label: 'Sourcing', terminal: false },
  'requisition:APPROVED': { tone: 'success', label: 'Approved', terminal: false },
  'po:PARTIALLY_RECEIVED': { tone: 'info', label: 'Partially received', terminal: false },
  'po:RECEIVED': { tone: 'success', label: 'Received', terminal: true },
  'receipt:INSPECTING': { tone: 'caution', label: 'Inspecting', terminal: false },
  'receipt:POSTED': { tone: 'success', label: 'Posted to stock', terminal: true },
  'inventory:QUARANTINE': { tone: 'caution', label: 'Quarantined', terminal: false },
  'inventory:AVAILABLE': { tone: 'success', label: 'Available', terminal: false },
  'inventory:REJECTED': { tone: 'critical', label: 'Rejected', terminal: true },
  'bill:MATCHED': { tone: 'success', label: 'Matched', terminal: false },
  'bill:EXCEPTION': { tone: 'caution', label: 'Match exception', terminal: false },
  'exception:OPEN': { tone: 'caution', label: 'Open exception', terminal: false },
  'exception:RESOLVED': { tone: 'success', label: 'Resolved', terminal: true },
  // order to cash + compliance
  'order:CONFIRMED': { tone: 'info', label: 'Confirmed', terminal: false },
  'order:ALLOCATED': { tone: 'info', label: 'Allocated', terminal: false },
  'order:SHORT': { tone: 'critical', label: 'Allocation short', terminal: false },
  'delivery:DISPATCHED': { tone: 'info', label: 'Dispatched', terminal: false },
  'delivery:DELIVERED': { tone: 'success', label: 'Delivered', terminal: true },
  'taxdoc:DRAFT': { tone: 'caution', label: 'Draft', terminal: false },
  'taxdoc:ISSUED': { tone: 'success', label: 'Issued', terminal: false },
  'taxdoc:CANCELLED': { tone: 'critical', label: 'Cancelled', terminal: true },
  'taxperiod:OPEN': { tone: 'caution', label: 'Open', terminal: false },
  'taxperiod:LOCKED': { tone: 'success', label: 'Locked', terminal: true },
  'taxperiod:FILED': { tone: 'success', label: 'Filed', terminal: true },
  'acctevent:PENDING': { tone: 'caution', label: 'Pending', terminal: false },
  'acctevent:DISPATCHED': { tone: 'info', label: 'Dispatched', terminal: false },
  'acctevent:ACKNOWLEDGED': { tone: 'success', label: 'Acknowledged', terminal: true },
  'acctevent:FAILED': { tone: 'critical', label: 'Failed', terminal: false },
  'recon:BALANCED': { tone: 'success', label: 'Balanced', terminal: true },
  'recon:DIFFERENCE': { tone: 'critical', label: 'Difference', terminal: false },
  // provenance / data quality (kept distinct from workflow status)
  'quality:GOOD': { tone: 'success', label: 'Good', terminal: false },
  'quality:UNCERTAIN': { tone: 'caution', label: 'Uncertain', terminal: false },
  'quality:BAD': { tone: 'critical', label: 'Bad', terminal: false },
  'quality:STALE': { tone: 'offline', label: 'Stale', terminal: false },
};

/** Resolve a status to its tone/label/terminal for any object type. */
export function statusOf(objectType: string, raw: string | null | undefined): StatusMeta {
  if (raw == null || raw === '') return { tone: 'neutral', label: '—', terminal: false };
  const hit = REGISTRY[`${objectType}:${raw.toUpperCase()}`];
  if (hit) return hit;
  return { tone: fallbackTone(raw), label: titleCase(raw), terminal: TERMINAL.test(raw) };
}
